import { Controller } from "@hotwired/stimulus"

/**
 * Business Info Watcher Controller
 *
 * Watches business information fields and dynamically unlocks/locks
 * the Revenue Verification section when required fields are complete.
 *
 * This provides real-time feedback without requiring page refresh,
 * solving the Turbo caching issue where the section stays locked
 * even after fields are filled.
 */
export default class extends Controller {
  static targets = ["revenueSection", "lockedMessage"]

  connect() {
    // Watch for changes to business info fields
    this.checkBusinessInfoComplete()

    // Set up mutation observer to detect Turbo Stream updates
    this.setupMutationObserver()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  /**
   * Check if all required business fields are complete
   */
  checkBusinessInfoComplete() {
    const requiredFields = [
      'loan_application_business_name',
      'loan_application_ein',
      'loan_application_industry',
      'loan_application_years_in_business',
      'loan_application_business_start_date',
      'loan_application_entity_type',
      'loan_application_use_of_funds',
      'loan_application_business_address',
      'loan_application_business_city',
      'loan_application_business_state',
      'loan_application_business_zip_code'
    ]

    let allComplete = true

    for (const fieldId of requiredFields) {
      const field = document.getElementById(fieldId)
      if (!field || !field.value.trim()) {
        allComplete = false
        break
      }
    }

    this.updateRevenueSectionState(allComplete)
  }

  /**
   * Update the revenue section visibility based on completion state
   */
  updateRevenueSectionState(isComplete) {
    if (!this.hasRevenueSectionTarget) {
      // Targets may not be available yet, find them manually
      const revenueSection = this.element.querySelector('[data-business-info-watcher-target="revenueSection"]')
      const lockedMessage = this.element.querySelector('[data-business-info-watcher-target="lockedMessage"]')

      if (revenueSection && lockedMessage) {
        if (isComplete) {
          lockedMessage.classList.add('hidden')
          revenueSection.classList.remove('hidden')
        } else {
          lockedMessage.classList.remove('hidden')
          revenueSection.classList.add('hidden')
        }
      }
    } else {
      if (isComplete) {
        this.lockedMessageTarget.classList.add('hidden')
        this.revenueSectionTarget.classList.remove('hidden')
      } else {
        this.lockedMessageTarget.classList.remove('hidden')
        this.revenueSectionTarget.classList.add('hidden')
      }
    }
  }

  /**
   * Watch for any form field changes
   */
  fieldChanged() {
    this.checkBusinessInfoComplete()
  }

  /**
   * Setup mutation observer to detect Turbo Stream DOM updates
   * This ensures the check runs after Turbo Stream replacements
   */
  setupMutationObserver() {
    this.observer = new MutationObserver(() => {
      // Re-check after DOM mutations (e.g., Turbo Stream updates)
      this.checkBusinessInfoComplete()
    })

    // Observe the entire form for changes
    this.observer.observe(this.element.closest('form') || this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value']
    })
  }
}
